/**
 * Firebase認証状態管理用のReactコンテキスト
 * カスタムトークンでのログイン処理を実装（セキュア版）
 */
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { csrfTokenService } from '../services/csrfTokenService';
import { getEnvironmentConfig, ENDPOINTS } from '../config/apiEndpoints';
import { setCookie, getCookie, removeCookie } from '../utils/cookieUtils';
import cookieApiService from '../services/cookieApiService';

// 認証コンテキストの作成
const AuthContext = createContext();
const CARD_REGISTRATION_FLAG = 'card_registration_in_progress';

/**
 * 認証コンテキストを使用するカスタムフック
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * 認証プロバイダーコンポーネント
 */
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isCardRegistrationInProgressRef = useRef(false);

  // 会員データの状態
  const [memberData, setMemberData] = useState(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberError, setMemberError] = useState(null);

  // CSRF管理状態
  const [csrfToken, setCsrfToken] = useState(null);
  const [csrfTokenCache, setCsrfTokenCache] = useState(new Map());

  /**
   * RegistrationFlowContext の状態をクリアする関数
   * セッションストレージからクッキーベースに変更
   */
  const clearRegistrationFlowState = () => {
    try {
      // RegistrationFlowContext が使用するクッキーキーを削除
      const registrationCookieKeys = [
        'registration_current_step',
        'registration_completed_steps', 
        'registration_data',
        'registration_flow_completed', 
        'termsAgreementSession',
        'stepCompletionTimes',
        'currentStepToken'
      ];
      
      registrationCookieKeys.forEach(key => {
        removeCookie(key);
      });

    } catch (error) {
      console.error('[AUTH CONTEXT] Failed to clear registration flow state:', error);
    }
  };

  /**
   * バックエンドAPIを使用して3Dセキュアクッキーをクリア
   */
  const clear3DSecureCookies = async () => {
    try {
      const cookieKeys = [
        'threeDSAuthId',
        'threeDSMemberId',
        'threeDSReturnUrl',
        'threeDSStoreCost',
        'threeDSMemberData'
      ];

      await cookieApiService.clearMultiple(cookieKeys);
    } catch (error) {
      console.error('[AUTH CONTEXT] Failed to clear 3D Secure cookies:', error);
    }
  };

/**
 * クッキーベースでCSRFトークンを取得（認証済みユーザー用）
 */
const getCsrfTokenFromCookie = async (forceRefresh = false) => {
  try {
    if (!forceRefresh && csrfToken) {
      return csrfToken;
    }
    const newCsrfToken = await csrfTokenService.generateTokenFromCookie();
    
    if (newCsrfToken) {
      setCsrfToken(newCsrfToken);
      return newCsrfToken;
    }

    console.warn('[AUTH CONTEXT] Failed to obtain CSRF token from cookie');
    return null;

  } catch (error) {
    console.error('[AUTH CONTEXT] Error getting CSRF token from cookie:', error);
    return null;
  }
};

  /**
   * CSRFトークンをクリア（ログアウト時など）
   */
  const clearCsrfToken = () => {
    setCsrfToken(null);
    setCsrfTokenCache(new Map());
  };

  /**
   * ユーザーセッションをクリア（クレカ登録用：トークン保持版）
   * 3Dセキュア認証で必要なトークンは保持する
   */
  const clearUserSessionForCardRegistration = () => {
    return new Promise((resolve) => {

      // Refフラグを即座にON（再レンダリングをトリガーしない）
      isCardRegistrationInProgressRef.current = true;

      // クッキーにもフラグを保存（3Dセキュア画面遷移後も保持）
      setCookie(CARD_REGISTRATION_FLAG, 'true', 30 / (24 * 60)); // 30分有効
      
      setCurrentUser(null);
      setMemberData(null);
      setMemberError(null);
      
      // 状態更新が完了するまで待機
      setTimeout(() => {
        resolve();
      }, 100);
    });
  };

  /**
   * カード登録完了後にフラグをクリア
   */
  const clearCardRegistrationFlag = () => {
    isCardRegistrationInProgressRef.current = false;
    removeCookie(CARD_REGISTRATION_FLAG);
  };

  /**
   * カード登録中かどうかをチェック
   */
  const isCardRegistrationInProgress = () => {
    const cookieFlag = getCookie(CARD_REGISTRATION_FLAG) === 'true';
    const refFlag = isCardRegistrationInProgressRef.current;
    const inProgress = cookieFlag || refFlag;
    
    return inProgress;
  };


  /**
   * ログイン関数
   */
  const login = async (loginData) => {

    try {

      if (isCardRegistrationInProgress()) {
        clearCardRegistrationFlag();
      }
      const config = getEnvironmentConfig();
      const loginUrl = `${config.baseUrl}/api/auth/login`;
      
      // 匿名CSRFトークンを取得
      const anonymousCsrfToken = await csrfTokenService.fetchAnonymousCsrfToken();
      
      if (!anonymousCsrfToken) {
        throw new Error('CSRFトークンの取得に失敗しました');
      }
      
      // リクエストボディを明示的に作成
      const requestBody = {
        email: loginData.email,
        password: loginData.password
      };
      
      const requestBodyString = JSON.stringify(requestBody);
      
      // バックエンドのログインAPIを呼び出し
      const response = await fetch(loginUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': anonymousCsrfToken
        },
        body: requestBodyString
      });
      
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('[AUTH CONTEXT] Failed to parse response as JSON:', e);
        throw new Error('サーバーからの応答が正しくありません');
      }
      
      if (!response.ok || !data.success) {
        const errorMessage = data.message || data.error || `ログインに失敗しました (Status: ${response.status})`;
        throw new Error(errorMessage);
      }
      
      // ログイン成功後、/api/meで会員データを取得
      const memberInfo = await fetchMemberDataFromApi();
      
      if (!memberInfo) {
        throw new Error('会員情報の取得に失敗しました');
      }
      
      // ユーザー状態を設定
      const authenticatedUser = {
        uid: memberInfo.id || memberInfo.memberId,
        email: memberInfo.email,
        displayName: memberInfo.fullName || memberInfo.name,
        emailVerified: true
      };
      
      setCurrentUser(authenticatedUser);
      setMemberData(memberInfo);
      
      return memberInfo;
      
    } catch (error) {
      console.error('[AUTH CONTEXT] Login failed:', error);
      setCurrentUser(null);
      setMemberData(null);
      throw error;
    }
  };

  /**
   * /api/me から会員データを取得する新しい関数
   */
  const fetchMemberDataFromApi = async () => {
    if (isCardRegistrationInProgress()) {
      return null;
    }
    try {
    setMemberLoading(true);
    setMemberError(null);
    
    const config = getEnvironmentConfig();
    const apiUrl = `${config.baseUrl}/api/me`;
    
    // クッキーに保存されたトークンを使用してCSRFトークンを取得
    const currentCsrfToken = await csrfTokenService.generateTokenFromCookie(); // 新しいメソッド
    
    if (!currentCsrfToken) {
      throw new Error('CSRFトークンの取得に失敗しました');
    }
    
    // クッキーは自動送信されるのでtokenパラメータ不要
    const response = await fetch(apiUrl, {
      method: 'GET',
      credentials: 'include', // クッキーを含める
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': currentCsrfToken 
      }
    });
    
    const data = await response.json();
    
    if (!response.ok || !data.success) {
      throw new Error(data.message || '会員情報の取得に失敗しました');
    }
    
    const memberInfo = data.member || data.user || data;
    setMemberData(memberInfo);
    setCsrfToken(currentCsrfToken);
  
    return memberInfo;
    
  } catch (error) {
    console.error('[AUTH CONTEXT] Failed to fetch member data:', error);
    setMemberError(error.message);
    return null;
  } finally {
    setMemberLoading(false);
  }
};

  /**
   * ユーザーがログインしているかチェック
   */
  const isAuthenticated = () => {
    const authenticated = !!currentUser;
    return authenticated;
  };

  /**
   * 会員データを手動で再取得（/api/meを使用）
   */
  const refetchMemberData = async () => {

  try {
    const memberInfo = await fetchMemberDataFromApi();
    
    if (memberInfo) {
      // ✅ currentUserも設定する
      const authenticatedUser = {
        uid: memberInfo.id || memberInfo.memberId,
        email: memberInfo.email,
        displayName: memberInfo.fullName || memberInfo.name,
        emailVerified: true
      };
      
      setCurrentUser(authenticatedUser);
      
      return memberInfo;
    }
    
    console.warn('[AUTH CONTEXT] ⚠️ No member info returned');
    return null;
    
  } catch (error) {
    console.error('[AUTH CONTEXT] ❌ refetchMemberData failed:', error);
    return null;
  }
};

  /**
   * 認証状態の監視
   */
  useEffect(() => {
    
    const initializeAuth = async () => {
      if (isCardRegistrationInProgress()) {
        setLoading(false);
        return;
      }

      try {
        // セッションクッキーがあるかチェック
        const config = getEnvironmentConfig();
        const apiUrl = `${config.baseUrl}/api/me`;
        
        // CSRFトークンを取得
        const csrfToken = await csrfTokenService.fetchAnonymousCsrfToken().catch(() => null);
        
        if (!csrfToken) {
          setLoading(false);
          return;
        }
        
        // /api/meを呼んでセッション確認
        const response = await fetch(apiUrl, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken 
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.member) {

            const memberInfo = data.member;
            
            // ユーザー状態を復元
            const restoredUser = {
              uid: memberInfo.id || memberInfo.memberId,
              email: memberInfo.email,
              displayName: memberInfo.fullName || memberInfo.name,
              emailVerified: true
            };
            
            setCurrentUser(restoredUser);
            setMemberData(memberInfo);
            setCsrfToken(csrfToken);
  
          } 
        } 
      } catch (error) {
        console.error('[AUTH CONTEXT] Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };
    
    initializeAuth();

    return () => {
      clearCsrfToken();
    };
  }, []);

  /**
   * ログアウト処理（バックエンドAPI経由）
   */
  const handleLogout = async () => {
    try {
      
      // バックエンドAPIにログアウトリクエスト
      const config = getEnvironmentConfig();
      const logoutUrl = `${config.baseUrl}${ENDPOINTS.AUTH.LOGOUT}`;
      
      const response = await fetch(logoutUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      await response.json();


      // 3Dセキュアクッキーをクリア
      await clear3DSecureCookies();
      
      // ローカル状態をクリア
      setCurrentUser(null);
      setMemberData(null);
      setMemberError(null);
      clearCsrfToken();
      
    } catch (error) {
      console.error('[AUTH CONTEXT] Logout failed:', error);
      // エラーでもローカル状態はクリア
      setCurrentUser(null);
      setMemberData(null);
      setMemberError(null);
      clearCsrfToken();
      clearRegistrationFlowState();
    }
  };

  /**
   * ユーザー名を取得する関数
   */
  const getUserDisplayName = () => {
    if (memberData?.fullName) return memberData.fullName;
    if (memberData?.name) return memberData.name;
    if (currentUser?.displayName) return currentUser.displayName;
    if (currentUser?.email) {
      return currentUser.email.split('@')[0];
    }
    return 'ゲスト';
  };

  /**
   * デバッグ用の情報取得
   * デバッグ情報を取得
   */
  const getDebugInfo = () => {
    return {
      isAuthenticated: isAuthenticated(),
      hasCurrentUser: !!currentUser,
      hasMemberData: !!memberData,
      hasError: !!memberError,
      isLoading: loading,
      userEmail: currentUser?.email,
      userUid: currentUser?.uid,
      memberName: memberData?.fullName || memberData?.name,
      memberId: memberData?.memberId,
      errorMessage: memberError,
      memberLoading,
      memberError,
      hasCsrfToken: !!csrfToken,
      displayName: getUserDisplayName(),
      csrfTokenPreview: csrfToken ? csrfToken.substring(0, 20) + '...' : null,
      csrfCacheSize: csrfTokenCache.size
    };
  };

  const value = {
    // 状態
    currentUser,
    loading,
    memberData,
    memberLoading,
    memberError,
    csrfToken,
    
    // アクション
    login,
    logout: handleLogout,
    getCsrfTokenFromCookie,
    clearCsrfToken,
    refetchMemberData,
    clearUserSessionForCardRegistration,
    clearCardRegistrationFlag,
    
    // ユーティリティ
    isAuthenticated,
    getUserDisplayName,
    getDebugInfo,
    isCardRegistrationInProgress
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;